import { useDialogVisible } from '@/hooks';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Textarea } from '@leadsnavi/ui-kit';
import { useMemoizedFn } from 'ahooks';
import cn from 'classnames';
import { useState } from 'react';
import CloseDialogIcon from '@/assets/icons/close-dialog.svg?react';

// ============================================================
// Types
// ============================================================

/** 反馈标签类型 */
export type FeedbackTag =
  | 'Not helpful'
  | 'Too short'
  | 'Too long'
  | 'Unsafe or problematic'
  | "Doesn't match my query"
  | 'Inaccurate data'
  | 'Incorrect source'
  | 'Others';

/** FeedbackFormDialog 组件 Props */
export interface FeedbackFormDialogProps {
  /** 是否打开 */
  open: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 提交回调 */
  onSubmit?: (data: { content: string; selectedTags: FeedbackTag[] }) => void | Promise<void>;
  /** 外部控制的 loading 状态 */
  loading?: boolean;
}

// ============================================================
// 常量
// ============================================================

/** 默认标签列表 */
const DEFAULT_TAGS: FeedbackTag[] = [
  'Not helpful',
  'Too short',
  'Too long',
  'Unsafe or problematic',
  "Doesn't match my query",
  'Inaccurate data',
  'Incorrect source',
  'Others',
];

// ============================================================
// TagItem 子组件
// ============================================================

interface TagItemProps {
  /** 标签文本 */
  label: string;
  /** 是否选中 */
  selected: boolean;
  /** 点击回调 */
  onClick: () => void;
}

/** 单个标签项组件 */
function TagItem(props: TagItemProps) {
  const { label, selected, onClick } = props;
  return (
    <div
      onClick={onClick}
      className={cn(
        'flex flex-row justify-center items-center gap-[10px]',
        'px-[10px] py-[5.5px] rounded-[8px] border border-[#E5E5E5]',
        'cursor-pointer select-none',
        'text-[14px] font-normal leading-[1.5em] tracking-[0.005em] text-[#262626]',
        selected ? 'bg-[#37E4A6]' : 'bg-white'
      )}
    >
      {label}
    </div>
  );
}

// ============================================================
// FeedbackFormDialog 组件
// ============================================================

/**
 * FeedbackFormDialog 反馈表单弹窗组件
 *
 * 基于 Dialog 封装的反馈表单，包含标签多选、文本描述输入，
 * 以及 Cancel / Submit 操作按钮。
 *
 * @example
 * ```tsx
 * <FeedbackFormDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   onSubmit={async ({ content, selectedTags }) => {
 *     await submitFeedback({ content, selectedTags });
 *   }}
 * />
 * ```
 */
export function FeedbackFormDialog(props: FeedbackFormDialogProps) {
  const { open, onClose, onSubmit, loading } = props;

  const [content, setContent] = useState('');
  const [selectedTags, setSelectedTags] = useState<FeedbackTag[]>([]);

  /** 提交按钮是否禁用：标签和内容均为空时禁用 */
  const btnDisabled = selectedTags.length === 0 && content.trim().length === 0;

  /** 处理文本域内容变化 */
  const handleContentChange = useMemoizedFn((value: string) => {
    setContent(value);
  });

  /** 切换标签选中状态 */
  const handleTagToggle = useMemoizedFn((tag: FeedbackTag) => {
    setSelectedTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
  });

  /** 处理提交 */
  const handleSubmit = useMemoizedFn(async () => {
    await onSubmit?.({ content, selectedTags });
  });

  /** 处理取消：重置表单并关闭 */
  const handleCancel = useMemoizedFn(() => {
    setContent('');
    setSelectedTags([]);
    onClose();
  });

  /** Dialog 关闭时重置表单状态 */
  const handleOpenChange = useMemoizedFn((isOpen: boolean) => {
    if (!isOpen) {
      setContent('');
      setSelectedTags([]);
      onClose();
    }
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="w-[480px] max-w-[480px] flex flex-col gap-0 p-0 rounded-[10px] overflow-hidden shadow-[0px_4px_6px_-4px_rgba(0,0,0,0.10),0px_10px_15px_-3px_rgba(0,0,0,0.10)]"
      >
        {/* 头部 */}
        <DialogHeader className="flex  items-center justify-between gap-[8px] p-[16px] flex-row!">
          <DialogTitle className="text-[20px] font-semibold leading-[1.2em] tracking-[-0.02em] text-[#171717]">
            What type of issue are you experiencing?
          </DialogTitle>
          <CloseDialogIcon
            className=" cursor-pointer"
            onClick={() => {
              handleOpenChange(false);
            }}
          />
        </DialogHeader>

        {/* 内容区域 */}
        <div className="flex flex-col gap-[12px] px-[20px]">
          {/* 副标题 */}
          <p className="text-[14px] font-normal leading-[1.5em] tracking-[0.005em] text-[#737373]">
            your feedback will help us improve your search results
          </p>

          {/* 标签选择区：Row 1 */}
          <div className="flex flex-row items-center gap-[8px]">
            {DEFAULT_TAGS.slice(0, 3).map(tag => (
              <TagItem
                key={tag}
                label={tag}
                selected={selectedTags.includes(tag)}
                onClick={() => handleTagToggle(tag)}
              />
            ))}
          </div>

          {/* 标签选择区：Row 2 */}
          <div className="flex flex-row items-center gap-[8px]">
            {DEFAULT_TAGS.slice(3, 5).map(tag => (
              <TagItem
                key={tag}
                label={tag}
                selected={selectedTags.includes(tag)}
                onClick={() => handleTagToggle(tag)}
              />
            ))}
          </div>

          {/* 标签选择区：Row 3 */}
          <div className="flex flex-row items-center gap-[8px]">
            {DEFAULT_TAGS.slice(5).map(tag => (
              <TagItem
                key={tag}
                label={tag}
                selected={selectedTags.includes(tag)}
                onClick={() => handleTagToggle(tag)}
              />
            ))}
          </div>
        </div>

        {/* Describe 文本域区域 */}
        <div className="flex flex-col gap-[4px] px-[20px] py-[16px]">
          <label className="text-[14px] font-medium leading-[1.5em] tracking-[0.005em] text-[#171717]">Describe</label>
          <Textarea
            value={content}
            onChange={e => handleContentChange(e.target.value)}
            placeholder="Describe the issue"
            className={cn(
              'w-full h-[76px]',
              'text-[14px] font-normal leading-[1.5em] tracking-[0.005em] text-[#737373]',
              'resize-none p-[8px] rounded-[8px]',
              'border border-[#D4D4D4] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
              'focus-visible:ring-0 focus-visible:ring-offset-0'
            )}
          />
        </div>

        {/* 底部按钮区 */}
        <DialogFooter className="flex flex-row justify-end items-center gap-[8px] px-[16px] py-[16px] border-t border-[#E5E5E5]">
          <Button
            variant="outline"
            onClick={handleCancel}
            className={cn(
              'flex flex-row justify-center items-center gap-[8px]',
              'px-[16px] py-[7.5px] rounded-[8px]',
              'border border-[#E5E5E5]',
              'text-[14px] font-medium leading-[1.5em] tracking-[0.005em] text-[#171717]',
              'h-auto shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)]'
            )}
          >
            Cancel
          </Button>
          <Button
            disabled={btnDisabled}
            onClick={handleSubmit}
            loading={loading}
            className={cn(
              'flex flex-row justify-center items-center gap-[8px]',
              'px-[16px] py-[7.5px] rounded-[8px] w-[114px]',
              'bg-[#171717] text-white',
              'text-[14px] font-medium leading-[1.5em] tracking-[0.005em]',
              'h-auto'
            )}
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const FeedbackDialogV2: React.FC<FeedbackFormDialogProps> = props => {
  const openV2 = useDialogVisible(props.open);
  if (!openV2) {
    return null;
  }
  return <FeedbackFormDialog {...props} />;
};
